import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';

import { FboMonolithAdminDataService } from '@common/data-services/fbo-monolith-admin-data/fbo-monolith-admin-data.service';
import { BusinessConfigurationParams } from '@common/data-services/fbo-monolith-admin-data/models/business-configuration-params.model';
import { CodeValueGroups, CodeValueService } from '@common/index';
import { PhxConstants } from '@common/PhoenixCommon.module';
import { IGrossMarginDetails, IWorkOrder } from '@workorder/models';

export interface IBusinessConfigurationDeductionData {
  ApplyToTransaction: boolean;
  DeductionData: IDeductionData[];
}

export interface IDeductionData {
  EffectiveDate: string;
  DeductionItems: IDeductionItem[];
}

export interface IDeductionItem {
  AmountFrom: number;
  AmountTo: number;
  RatePercent: number;
  WorkLocationCodes: string[];
}

@Injectable()
export class WorkOrderGrossMarginService {

  grossMarginDetailsUpdated$ = new BehaviorSubject<IGrossMarginDetails>(null);

  isDestroyed$ = new Subject<boolean>();

  grossMarginDetails: IGrossMarginDetails;
  effectiveEstimatedPayrollDeductionItem: IDeductionItem;
  effectiveEstimatedPayrollDeductionItems: IDeductionItem[];
  workerLocationCode: string;
  workOrderVersionEffectiveDate: string;
  profileType: number;
  IsT4SalariedBillSideWorkOrder: boolean;

  constructor(private readonly fboMonolithAdminDataService: FboMonolithAdminDataService, private readonly codeValueService: CodeValueService) { }

  initGrossMarginDetails(workOrder: IWorkOrder, isDestroyed$: Subject<boolean>) {
    this.isDestroyed$ = isDestroyed$;
    this.profileType = workOrder.workerProfileTypeId;
    this.workOrderVersionEffectiveDate = workOrder.WorkOrderVersion.EffectiveDate;
    this.workerLocationCode = this.codeValueService.getCodeValueCode(workOrder.WorkOrderVersion.WorkerLocationId, CodeValueGroups.Subdivision);
    this.IsT4SalariedBillSideWorkOrder = workOrder.RootObject?.IsT4SalariedBillSideWorkOrder ?? false;

    this.setGrossMarginDetails(workOrder);
    
    if (!this.IsT4SalariedBillSideWorkOrder) {
      this.updateEstimatedPayrollDeduction(workOrder.OrganizationIdInternal, workOrder.WorkOrderVersion.LineOfBusinessId, workOrder.WorkOrderVersion.InternalOrganizationDefinition1Id);
    }
  }

  setGrossMarginDetails(workOrder: IWorkOrder) {
    const workOrderVersion = workOrder.WorkOrderVersion;

    const primaryBillRateInfo = workOrderVersion.BillingInfoes[0].BillingRates.find(x => x.RateTypeId === PhxConstants.RateType.Primary);
    const primaryPayRateInfo = workOrderVersion.PaymentInfoes[0].PaymentRates.find(x => x.RateTypeId === PhxConstants.RateType.Primary);

    const primaryBillRate = +primaryBillRateInfo.Rate;

    this.grossMarginDetails = {
      HoursPerDay: workOrderVersion.BillingInfoes[0].Hours,
      ConvertedPrimaryBillRate: null,
      PrimaryBillRate: primaryBillRate,
      PrimaryBillRateUnit: primaryBillRateInfo.RateUnitId,      
      ConvertedVmsFee: null,
      VmsFee: +this.getVmsFeeOrRebate(workOrderVersion.HasVmsFee, workOrderVersion.VmsFeeTypeId, workOrderVersion.VmsFeeRate, primaryBillRate),
      ConvertedRebate: null,
      Rebate: +this.getVmsFeeOrRebate(workOrderVersion.HasRebate, workOrderVersion.RebateTypeId, workOrderVersion.RebateRate, primaryBillRate),
      ConvertedPrimaryPayRate: null,
      PrimaryPayRate: +primaryPayRateInfo.Rate, 
      PrimaryPayRateUnit: primaryPayRateInfo.RateUnitId,
      EstimatedPayrollDeduction: 0,
      GrossMargin: 0,
      GrossMarginPercentage: 0,
      // T4 Salaried Bill Side Work Order specific properties
      ShowGrossMarginPercentage: !this.IsT4SalariedBillSideWorkOrder,
      PayRateLabel: this.IsT4SalariedBillSideWorkOrder ? 'Reporting Pay Rate' : 'Primary Pay Rate'
    };

    this.convertGrossMarginAmounts();

    if (!this.IsT4SalariedBillSideWorkOrder) {
      this.calculateEstimatedPayrollDeduction();
    }
    this.calculateGrossMargin();
  }

  updateWorkerLocation(workerLocationId: number) {
    this.workerLocationCode = this.codeValueService.getCodeValueCode(workerLocationId, CodeValueGroups.Subdivision);
    
    if (!this.IsT4SalariedBillSideWorkOrder) {
      this.updateEffectiveEstimatedPayrollDeductionItem();
    }
  }

  updateEstimatedPayrollDeduction(internalOrganizationId: number, lineOfBusinessId: number, branchId: number) {
    const businessConfigParams: BusinessConfigurationParams = {
      internalOrganizationId,
      lineOfBusinessId,
      internalOrganizationDefinitionId1: branchId,
      workerProfileTypeId: this.profileType
    };

    this.fboMonolithAdminDataService.getBusinessRuleConfiguration(PhxConstants.BusinessConfigurationKey.EstimatedPayrollDeduction, businessConfigParams, false)
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe((data: string) => {
        const estimatedPayrollDeductionData: IBusinessConfigurationDeductionData = data ? JSON.parse(data) : { ApplyToTransaction: false, DeductionData: [] };
        this.effectiveEstimatedPayrollDeductionItems = estimatedPayrollDeductionData.DeductionData
          .filter(x => new Date(x.EffectiveDate) <= new Date(this.workOrderVersionEffectiveDate))
          .sort((a, b) => new Date(b.EffectiveDate).getTime() - new Date(a.EffectiveDate).getTime())[0]
          ?.DeductionItems ?? [];

        this.updateEffectiveEstimatedPayrollDeductionItem();
      });
  }

  private convertGrossMarginAmounts() {
    if (this.grossMarginDetails.PrimaryBillRateUnit === PhxConstants.RateUnit.Day) {
      this.grossMarginDetails.ConvertedPrimaryBillRate = this.grossMarginDetails.PrimaryBillRate / this.grossMarginDetails.HoursPerDay;
      this.grossMarginDetails.ConvertedVmsFee = this.grossMarginDetails.VmsFee / this.grossMarginDetails.HoursPerDay;
      this.grossMarginDetails.ConvertedRebate = this.grossMarginDetails.Rebate / this.grossMarginDetails.HoursPerDay;
    }

    if (this.grossMarginDetails.PrimaryPayRateUnit === PhxConstants.RateUnit.Day) {
      this.grossMarginDetails.ConvertedPrimaryPayRate = this.grossMarginDetails.PrimaryPayRate / this.grossMarginDetails.HoursPerDay;
    }
  }

  private updateEffectiveEstimatedPayrollDeductionItem() {
    const primaryPayRate = this.grossMarginDetails.ConvertedPrimaryPayRate ?? this.grossMarginDetails.PrimaryPayRate;

    this.effectiveEstimatedPayrollDeductionItem = this.effectiveEstimatedPayrollDeductionItems?.find((x: IDeductionItem) =>
      primaryPayRate > x.AmountFrom
      && (!x.AmountTo || primaryPayRate <= x.AmountTo)
      && (!x.WorkLocationCodes?.length || x.WorkLocationCodes.some(c => c === this.workerLocationCode)));

    if (!this.IsT4SalariedBillSideWorkOrder) {
      this.calculateEstimatedPayrollDeduction();
    }
    this.calculateGrossMargin();
  }

  private calculateEstimatedPayrollDeduction() {
    this.grossMarginDetails.EstimatedPayrollDeduction = this.effectiveEstimatedPayrollDeductionItem
      ? (this.grossMarginDetails.ConvertedPrimaryPayRate ?? this.grossMarginDetails.PrimaryPayRate) * (this.effectiveEstimatedPayrollDeductionItem.RatePercent / 100)
      : 0;
  }

  private calculateGrossMargin() {
    if (this.IsT4SalariedBillSideWorkOrder) {
      // Simplified calculation for T4 salaried bill side: just primary bill rate - primary pay rate
      this.grossMarginDetails.GrossMargin = this.grossMarginDetails.PrimaryBillRate - this.grossMarginDetails.PrimaryPayRate;
    } else {
      const adjustedBillRate = (this.grossMarginDetails.ConvertedPrimaryBillRate ?? this.grossMarginDetails.PrimaryBillRate)
        - (this.grossMarginDetails.ConvertedVmsFee ?? this.grossMarginDetails.VmsFee);

      this.grossMarginDetails.GrossMargin = adjustedBillRate
        - (this.grossMarginDetails.ConvertedRebate ?? this.grossMarginDetails.Rebate)
        - (this.grossMarginDetails.ConvertedPrimaryPayRate ?? this.grossMarginDetails.PrimaryPayRate)
        - this.grossMarginDetails.EstimatedPayrollDeduction;

      this.grossMarginDetails.GrossMarginPercentage = (this.grossMarginDetails.GrossMargin / adjustedBillRate) * 100;
    }

    this.grossMarginDetailsUpdated$.next(this.grossMarginDetails);
  }

  private getVmsFeeOrRebate(hasRate: boolean, type: number, rate: number, amount: number): number {
    if (hasRate === true && rate) {
      return type === PhxConstants.RebateType.Percentage ? amount * (rate / 100) : rate;
    }

    return 0;
  }
}
